import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  getHandlerRequestsByHandlerId,
  approveHandlerRequest,
  rejectHandlerRequest,
  SaveParticipant,
} from '../api/actions';
import { getLoggedInParticipant } from '../api/participantAuth';
import { useParticipants } from '../hooks/useParticipants';
import { calculateHandlerOpenSlots, getHandlerCapacitySummary } from '../utils/handlerCapacity';
import { getApprovedBirdsForHandler } from '../utils/handlerSlots';
import HandlersRequestsTable from '../../../components/HandlersRequestsTable';
import RejectRequestModal from './RejectRequestModal';

export default function HandlerTab({ 
  participant, 
  onMessage, 
  onParticipantUpdate,
  isEditing,
  onEditingChange,
  saving,
  setSaving 
}) {
  const [handlersRequestsReceived, setHandlersRequestsReceived] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRejectRequest, setSelectedRejectRequest] = useState(null);
  const [showExceedLimitModal, setShowExceedLimitModal] = useState(false);
  const [pendingApprovalRequest, setPendingApprovalRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const { participants: allParticipants } = useParticipants();

  // Local state for editing (mirrors PersonalInfoTab pattern)
  const [editedData, setEditedData] = useState({});

  useEffect(() => {
    if (participant) {
      setEditedData({
        ...participant,
        handlerInfo: participant.handlerInfo || {}
      });
    }
  }, [participant]);

  useEffect(() => {
    // When exiting edit mode, ensure we have the latest data
    if (!isEditing && participant) {
      setEditedData(participant);
    }
  }, [isEditing, participant]);

  useEffect(() => {
    if (participant && hasRole('handler')) {
      loadHandlerRequests();
    }
  }, [participant]);



  const hasRole = (roleToCheck) => {
    if (Array.isArray(participant?.role)) {
      return participant.role.some(r => r.toLowerCase() === roleToCheck.toLowerCase());
    }
    return participant?.role?.toLowerCase() === roleToCheck.toLowerCase();
  };

  const formatSection = (section) => {
    if (!section || typeof section !== 'string') return 'N/A';
    const s = section.trim().toLowerCase();
    if (s === 'north') return 'North';
    if (s === 'central') return 'Central';
    if (s === 'south') return 'South';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const loadHandlerRequests = async () => {
    try {
      setLoading(true);
      const handlerResult = await getHandlerRequestsByHandlerId(participant.id);
      if (Array.isArray(handlerResult)) {
        setHandlersRequestsReceived(handlerResult);
      } else if (handlerResult && Array.isArray(handlerResult.requests)) {
        setHandlersRequestsReceived(handlerResult.requests);
      } else {
        setHandlersRequestsReceived([]);
      }
    } catch (error) {
      setHandlersRequestsReceived([]);
      if (onMessage) {
        onMessage('Failed to load handler requests. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (requestId, bypassLimitCheck = false) => {
    try {
      if (!participant || !participant.handlerInfo) {
        onMessage('Handler information not available. Please refresh the page.', 'error');
        return;
      }

      const request = handlersRequestsReceived.find(r => r.id === requestId);
      if (!request) {
        onMessage('Request not found.', 'error');
        return;
      }

      const currentOpenSlots = participant.handlerInfo?.openSlots || 0;
      const birdsRequested = parseInt(request.numberOfBirds || request.birdsRequested) || 0;
      const totalBirdsCapacity = participant.handlerInfo?.totalBirds || 0;
      const alreadyApproved = getApprovedBirdsForHandler(
        handlersRequestsReceived,
        participant.id
      );
      const remainingSlots = totalBirdsCapacity - alreadyApproved;

      // Check if this would exceed the bird limit
      if (remainingSlots < birdsRequested && !bypassLimitCheck) {
        // Show confirmation modal for exceeding limit
        setPendingApprovalRequest(request);
        setShowExceedLimitModal(true);
        return;
      }

      // Proceed with approval
      const result = await approveHandlerRequest(requestId, participant.id);
      
      if (result.success) {
        // Update participant's openSlots
        const updatedParticipant = {
          ...participant,
          handlerInfo: {
            ...participant.handlerInfo,
            openSlots: remainingSlots - birdsRequested
          }
        };

        // Save updated participant data
        const authData = getLoggedInParticipant();
        await SaveParticipant(updatedParticipant, authData?.authToken);

        if (onMessage) {
          onMessage(
            remainingSlots - birdsRequested < 0 
              ? `Request approved! You are now over capacity by ${Math.abs(remainingSlots - birdsRequested)} birds.`
              : 'Request approved successfully!',
            'success'
          );
        }

        // Reload requests and trigger participant data update
        await loadHandlerRequests();
        if (onParticipantUpdate) {
          onParticipantUpdate();
        }
      } else {
        if (onMessage) {
          onMessage(`Failed to approve request: ${result.message}`, 'error');
        }
      }
    } catch (error) {
      if (onMessage) {
        onMessage('An error occurred while approving the request.', 'error');
      }
    }
  };

  const handleExceedLimitConfirm = async () => {
    setShowExceedLimitModal(false);
    if (pendingApprovalRequest) {
      await handleApproveRequest(pendingApprovalRequest.id, true); // Bypass limit check
      setPendingApprovalRequest(null);
    }
  };

  const handleExceedLimitCancel = () => {
    setShowExceedLimitModal(false);
    if (pendingApprovalRequest) {
      // Show reject modal instead
      setSelectedRejectRequest(pendingApprovalRequest);
      setShowRejectModal(true);
      setPendingApprovalRequest(null);
    }
  };

  const handleRejectClick = (request) => {
    // Enrich request with breeder information if not present
    const breeder = allParticipants?.find(p => p.id?.toString() === request.breederId?.toString());
    
    const enrichedRequest = {
      ...request,
      breederName: request.breederName || breeder?.fullName || 'Unknown Breeder',
      breederLoftName: request.breederLoftName || breeder?.loftName || 'Unknown Loft'
    };
    
    setSelectedRejectRequest(enrichedRequest);
    setShowRejectModal(true);
  };

  const handleRejectRequest = async (reason) => {
    try {
      if (!participant || !participant.id) {
        onMessage('Participant information not available. Please refresh the page.', 'error');
        return;
      }
      
      const result = await rejectHandlerRequest(selectedRejectRequest.id, participant.id, reason);
      
      if (result.success) {
        if (onMessage) {
          onMessage('Request rejected and breeder has been notified.');
        }
        await loadHandlerRequests();
        setShowRejectModal(false);
        setSelectedRejectRequest(null);
      } else {
        if (onMessage) {
          onMessage(`Failed to reject request: ${result.message}`);
        }
      }
    } catch (error) {
      if (onMessage) {
        onMessage('An error occurred while rejecting the request.');
      }
    }
  };

  const handleSave = async () => {
    // Set saving first to disable the save button immediately
    if (setSaving) {
      setSaving(true);
    }
    
    try {
      const authData = getLoggedInParticipant();
      const result = await SaveParticipant(editedData, authData?.authToken);
      
      // API returns participant object directly, not wrapped in success object
      if (result && result.id) {
        // Exit edit mode immediately
        if (onEditingChange) {
          onEditingChange(false);
        }
        
        // Show loading message while reloading data
        if (onMessage) {
          onMessage('Loading updated information...');
        }
        
        // Trigger participant data update
        if (onParticipantUpdate) {
          await onParticipantUpdate();
        }
        
        // Show success message after data is loaded
        if (onMessage) {
          onMessage('Handler information updated successfully!');
        }
      } else {
        if (onMessage) {
          onMessage(result?.message || 'Failed to update handler information. Please try again.');
        }
        // Go back to edit mode if save failed
        if (onEditingChange) {
          onEditingChange(true);
        }
      }
    } catch (error) {
      if (onMessage) {
        onMessage('An error occurred while saving handler information.');
      }
      // Go back to edit mode if error occurred
      if (onEditingChange) {
        onEditingChange(true);
      }
    } finally {
      if (setSaving) {
        setSaving(false);
      }
    }
  };

  const handleContactMethodChange = (method, checked) => {
    const currentMethods = editedData.handlerInfo?.breederApplicationMethod || [];
    const newMethods = checked 
      ? [...currentMethods.filter(m => m !== method), method]
      : currentMethods.filter(m => m !== method);
    
    setEditedData(prev => ({
      ...prev,
      handlerInfo: {
        ...(prev.handlerInfo || {}),
        breederApplicationMethod: newMethods
      }
    }));
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      handlerInfo: {
        ...(prev.handlerInfo || {}),
        [field]: value
      }
    }));
  };

  // Register save handler with parent component (matches PersonalInfoTab pattern)
  useEffect(() => {
    if (isEditing) {
      // Handler tab uses global save controls
      window.handlerTabSave = handleSave;
    }
    return () => {
      delete window.handlerTabSave;
    };
  }, [isEditing, editedData]);

  // Check if participant data is available
  if (!participant) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading participant information...</div>
      </div>
    );
  }

  if (!hasRole('handler')) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        You need to have the Handler role to access this section. You can add the Handler role in the Personal Info tab.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading handler information...</div>
      </div>
    );
  }

  const totalBirdsCapacity = participant.handlerInfo?.totalBirds || 0;
  const approved = getApprovedBirdsForHandler(handlersRequestsReceived, participant.id);
  const pending = handlersRequestsReceived
    .filter(r => r.handlerId === participant.id && r.status === 'pending')
    .reduce((sum, r) => sum + (r.numberOfBirds || 0), 0);
  const openSlots = totalBirdsCapacity - approved;

  return (
    <>
      {/* Remove Current Available Slots Badge */}

      {/* Handler Bird Distribution Dashboard */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-bar-chart-fill me-2"></i>
            Bird Distribution (Handler)
          </h5>
        </div>
        <div className="card-body">
          <div className="row text-center">
            <div className="col">
              <div className="fw-bold">Capacity</div>
              <div className="display-6">{totalBirdsCapacity}</div>
              <div className="text-muted small">Total birds you can accept</div>
            </div>
            <div className="col">
              <div className="fw-bold">Approved</div>
              <div className="display-6 text-success">{approved}</div>
              <div className="text-muted small">Birds approved</div>
            </div>
            <div className="col">
              <div className="fw-bold">Pending</div>
              <div className="display-6 text-warning">{pending}</div>
              <div className="text-muted small">Birds in pending requests</div>
            </div>
            <div className="col">
              <div className="fw-bold">Available</div>
              <div className="display-6 text-primary">{openSlots}</div>
              <div className="text-muted small">Slots left</div>
            </div>
          </div>
        </div>
      </div>

      {/* Handler Requests Table */}
      <HandlersRequestsTable
        requests={handlersRequestsReceived}
        allParticipants={allParticipants}
        participant={participant}
        onApproveRequest={handleApproveRequest}
        onRejectClick={handleRejectClick}
      />

      {/* Exceed Limit Confirmation Modal */}
      <Modal show={showExceedLimitModal} onHide={() => setShowExceedLimitModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Bird Limit Exceeded
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingApprovalRequest && (
            <div>
              <div className="alert alert-warning">
                <strong>You have exceeded your bird limit.</strong>
                <br />
                Are you sure you want to proceed?
              </div>
              <div className="card bg-light">
                <div className="card-body">
                  <strong>Breeder:</strong> {(() => {
                    const breeder = allParticipants?.find(p => p.id === pendingApprovalRequest.breederId);
                    return breeder ? breeder.fullName : 'Unknown Breeder';
                  })()}
                  <br />
                  <strong>Birds Requested:</strong> {pendingApprovalRequest.numberOfBirds || pendingApprovalRequest.birdsRequested}
                  <br />
                  <strong>Current Available Slots:</strong> {participant.handlerInfo?.openSlots || 0}
                  <br />
                  <strong>Slots After Approval:</strong> <span className="text-danger">
                    {(participant.handlerInfo?.openSlots || 0) - (parseInt(pendingApprovalRequest.numberOfBirds || pendingApprovalRequest.birdsRequested) || 0)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleExceedLimitCancel}>
            Reject Instead
          </Button>
          <Button variant="warning" onClick={handleExceedLimitConfirm}>
            Approve Anyway
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Request Modal */}
      {showRejectModal && selectedRejectRequest && (
        <RejectRequestModal
          show={showRejectModal}
          onClose={() => {
            setShowRejectModal(false);
            setSelectedRejectRequest(null);
          }}
          request={selectedRejectRequest}
          onSubmit={handleRejectRequest}
        />
      )}
    </>
  );
}
